import { useState } from 'react';
import { Alert, Button, Modal, Radio, Tabs } from '@omnitok/ui';
import JsonViewer from '@/features/localization/components/JsonViewer';
import { buildModelPreview, chooseModels } from './utils';
import type { Candidate, Configuration, IndexadoPreview, ResultRow } from './types';
import IndexadoPreviewPanel from './IndexadoPreviewPanel';

export default function RowDialog({ row, tab, configuration, busy, onClose, onChoose, onIndexados }: {
  row: ResultRow; tab: string; configuration: Configuration; busy: boolean;
  onClose: () => void; onChoose: (row: ResultRow) => void;
  onIndexados: (indexados: IndexadoPreview[]) => void;
}) {
  const [activeTab, setActiveTab] = useState(tab);
  const [baseId, setBaseId] = useState(row.selection?.modelBase ?? (row.candidates.base.length === 1 ? row.candidates.base[0].id : ''));
  const [sourceId, setSourceId] = useState(row.selection?.modelToIntegrate ?? (row.candidates.toIntegrate.length === 1 ? row.candidates.toIntegrate[0].id : ''));
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const canChoose = row.status === 'error' && row.error?.code === 'AMBIGUOUS_MODELS'
    && row.candidates.base.length > 0 && row.candidates.toIntegrate.length > 0;
  const selectedBase = row.candidates.base.find(candidate => candidate.id === row.selection?.modelBase);
  const selectedSource = row.candidates.toIntegrate.find(candidate => candidate.id === row.selection?.modelToIntegrate);
  const tabs = [
    ...(row.error ? [{ id: 'error', label: 'Error' }] : []),
    ...(row.changes ? [{ id: 'changes', label: 'Cambios' }, { id: 'preview', label: 'Preview' }] : []),
    ...(row.selection ? [{ id: 'indexado-changes', label: 'Cambios Indexados' }, { id: 'indexado-preview', label: 'Preview Indexados' }] : []),
    ...(row.candidates.base.length || row.candidates.toIntegrate.length ? [{ id: 'models', label: 'Modelos' }] : []),
    ...(row.integration ? [{ id: 'result', label: 'Resultado' }] : []),
  ];
  const choose = () => {
    try { onChoose(chooseModels(row, baseId, sourceId, configuration)); onClose(); }
    catch (error) { setSelectionError(error instanceof Error ? error.message : 'No fue posible seleccionar los modelos.'); }
  };
  const candidateGroup = (title: string, models: Candidate[], selected: string, onSelect: (id: string) => void, name: string) => <section>
    <h3>{title}</h3>
    <div className="consolidation-candidates">
      {models.map(model => <div className={`localization-model-card${selected === model.id ? ' localization-model-card--selected' : ''}`} key={model.id}>
        {canChoose ? <Radio name={name} value={model.id} checked={selected === model.id} onChange={() => onSelect(model.id)} label={model.id} disabled={busy} />
          : <strong className="localization-mono">{model.id}</strong>}
        <p className="localization-muted">Localizaciones: {Object.keys(model.langs ?? {}).join(', ') || 'Sin idiomas'}</p>
        <JsonViewer value={model} fileName={`${row.baseSku}.${model.id}.json`} />
      </div>)}
      {!models.length && <p className="localization-muted">No se encontraron candidatos.</p>}
    </div>
  </section>;

  return <Modal className="localization-modal" open onClose={onClose} title={`Fila ${row.index + 1} · ${row.baseSku}`} size="full"
    footer={<><Button variant="outline" onClick={onClose}>Cerrar</Button>
      {canChoose && <Button variant="primary" disabled={busy || !baseId || !sourceId} onClick={choose}>Usar estos modelos</Button>}</>}>
    <div className="localization-dialog-content">
      <Tabs items={tabs} activeId={activeTab} onChange={setActiveTab} size="sm" variant="underline" />
      {selectionError && <Alert variant="error">{selectionError}</Alert>}
      {activeTab === 'error' && row.error && <>
        <Alert variant="error">{row.error.message}</Alert>
        <p><strong>Código:</strong> {row.error.code}{row.error.status ? ` · HTTP ${row.error.status}` : ''}</p>
        <p>SKU base: <strong>{row.baseSku}</strong> · SKU a integrar: <strong>{row.skuToIntegrate}</strong></p>
        {canChoose && <Button variant="outline" onClick={() => setActiveTab('models')}>Comparar y elegir modelos</Button>}
      </>}
      {activeTab === 'changes' && row.changes && <>
        <dl className="consolidation-change-list">
          <div><dt>Modelo que se conserva</dt><dd>{row.selection?.modelBase}</dd></div>
          <div><dt>Modelo que se integra</dt><dd>{row.selection?.modelToIntegrate}</dd></div>
          <div><dt>Contenido a copiar</dt><dd>{row.changes.sourcePath} → {row.changes.targetPath}</dd></div>
          <div><dt>Destino existente</dt><dd>{row.changes.targetExists ? 'Se reemplazará su contenido' : 'Se agregará la localización'}</dd></div>
        </dl>
        <Alert variant="info">Las fichas del modelo a integrar pasarán al modelo base, con la localización {configuration.targetLocale} y translated: true. El modelo integrado quedará inactivo.</Alert>
        <div className="consolidation-preview-columns">
          <section><h3>Contenido destino actual</h3><JsonViewer value={selectedBase?.langs?.[configuration.targetLocale] ?? null} fileName={`${row.baseSku}.antes.json`} /></section>
          <section><h3>Contenido que se copiará</h3><JsonViewer value={row.changes.localizedContent} fileName={`${row.baseSku}.despues.json`} /></section>
        </div>
      </>}
      {activeTab === 'preview' && <>
        <Alert variant="info">Vista propuesta del modelo base. También puedes revisar las fichas en “Cambios Indexados” y “Preview Indexados”.</Alert>
        <JsonViewer value={buildModelPreview(row, configuration)} fileName={`${row.baseSku}.preview.json`} />
        <details><summary>Modelo a integrar antes de la consolidación</summary><JsonViewer value={selectedSource} fileName={`${row.skuToIntegrate}.origen.json`} /></details>
      </>}
      {(activeTab === 'indexado-changes' || activeTab === 'indexado-preview') && <IndexadoPreviewPanel
        row={row} configuration={configuration} view={activeTab === 'indexado-changes' ? 'changes' : 'preview'} onLoaded={onIndexados} />}
      {activeTab === 'models' && <>
        {canChoose && <Alert variant="warning">Selecciona un modelo base y uno a integrar después de revisar sus datos. Esto prepara la fila para revisión; todavía no aplica cambios.</Alert>}
        {candidateGroup('Modelo base · se conserva', row.candidates.base, baseId, setBaseId, 'consolidation-base')}
        {candidateGroup('Modelo a integrar · se desactiva', row.candidates.toIntegrate, sourceId, setSourceId, 'consolidation-source')}
      </>}
      {activeTab === 'result' && <JsonViewer value={row.integration} fileName={`${row.baseSku}.resultado.json`} />}
    </div>
  </Modal>;
}
